import { useBulkReachOut } from "@entities/pricingWorkload/lib/hooks/useBulkReachOut";
import { BulkReachoutTemplateVersion } from "@shared/lib/types/pricingWorkload";
import { Button, Segmented } from "@shared/ui";
import { ChevronRight, MessageCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { buildBulkReachoutActionParams } from "./lib/buildActionParams";
import {
  BULK_REACHOUT_TEMPLATE_OPTIONS,
  DEFAULT_BULK_REACHOUT_TEMPLATE_VERSION,
} from "./lib/constants";
import { renderBulkReachoutTemplateHtml } from "./lib/renderTemplateHtml";
import styles from "./styles.module.scss";
import { BulkReachoutTemplateFields } from "./ui/BulkReachoutTemplateFields";

type BulkReachoutProps = {
  briefId: string;
  isSelectAll: boolean;
  ids: string[];
  notIncludeIds: string[];
  selectedCount: number;
  onClose: () => void;
  filterParams: { [key: string]: any };
  isResend: boolean;
};

export function BulkReachout({
  briefId,
  isSelectAll,
  ids,
  notIncludeIds,
  selectedCount,
  onClose,
  filterParams,
  isResend,
}: BulkReachoutProps) {
  const { bulkReachOut, isBulkReachOutLoading, isBulkReachOutSuccess } =
    useBulkReachOut();
  const [templateVersion, setTemplateVersion] =
    useState<BulkReachoutTemplateVersion>(
      DEFAULT_BULK_REACHOUT_TEMPLATE_VERSION
    );
  const [product, setProduct] = useState("");
  const [timeline, setTimeline] = useState("");
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [separateDeliverables, setSeparateDeliverables] = useState<string[]>(
    []
  );

  const canContinue = useMemo(() => selectedCount > 0, [selectedCount]);
  const templateHtml = useMemo(
    () =>
      renderBulkReachoutTemplateHtml(templateVersion, {
        product,
        timeline,
        additionalMessage,
        separateDeliverables,
      }),
    [
      templateVersion,
      product,
      timeline,
      additionalMessage,
      separateDeliverables,
    ]
  );

  useEffect(() => {
    if (isBulkReachOutSuccess) {
      onClose();
    }
  }, [isBulkReachOutSuccess, onClose]);

  const handleTemplateVersionChange = (value: string | number) => {
    const nextVersion = value as BulkReachoutTemplateVersion;
    if (nextVersion === templateVersion) {
      return;
    }

    setTemplateVersion(nextVersion);
    setProduct("");
    setTimeline("");
    setAdditionalMessage("");
    setSeparateDeliverables([]);
  };

  const handleContinue = () => {
    if (!canContinue) {
      return;
    }

    bulkReachOut({
      briefId,
      isSelectAll,
      ids,
      notIncludeIds,
      filterParams,
      isResend,
      actionParams: buildBulkReachoutActionParams({
        templateVersion,
        product,
        timeline,
        additionalMessage,
        separateDeliverables,
      }),
    });
  };

  if (selectedCount === 0) {
    return (
      <div className={styles.container}>
        <p className={styles.subtitle}>
          No creators selected. Close and pick rows to send a bulk reach-out.
        </p>
        <div className={styles.actions}>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <p className={styles.subtitle}>
        <strong>{selectedCount}</strong> creator
        {selectedCount !== 1 ? "s" : ""} selected · WhatsApp only
      </p>

      <div className={styles.whatsappBanner}>
        <MessageCircle size={18} className={styles.whatsappIcon} />
        <div>
          <p className={styles.whatsappTitle}>
            {isResend ? "Resend reach-out" : "Reach out via WhatsApp"}
          </p>
          <p className={styles.whatsappText}>
            {isResend
              ? "All selected creators will receive the resent outreach on WhatsApp. Email outreach is not available in this flow."
              : "All selected creators will receive the outreach on WhatsApp. Email outreach is not available in this flow."}
          </p>
        </div>
      </div>

      <div className={styles.templateSection}>
        <p className={styles.templateLabel}>Message template</p>
        <Segmented
          block
          value={templateVersion}
          options={BULK_REACHOUT_TEMPLATE_OPTIONS}
          onChange={handleTemplateVersionChange}
        />
        <BulkReachoutTemplateFields
          templateVersion={templateVersion}
          product={product}
          timeline={timeline}
          additionalMessage={additionalMessage}
          separateDeliverables={separateDeliverables}
          onProductChange={setProduct}
          onTimelineChange={setTimeline}
          onAdditionalMessageChange={setAdditionalMessage}
          onSeparateDeliverablesChange={setSeparateDeliverables}
        />
        <div
          className={styles.templatePreview}
          dangerouslySetInnerHTML={{ __html: templateHtml }}
        />
      </div>

      <div className={styles.actions}>
        <Button onClick={onClose} disabled={isBulkReachOutLoading}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={isBulkReachOutLoading}
          disabled={!canContinue}
          onClick={handleContinue}
          icon={<ChevronRight size={16} />}
          iconPosition="end"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

export default BulkReachout;
